import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { differenceInCalendarDays } from 'date-fns';
import CopyCheckbox from './copyWorkoutModal/CopyCheckbox';
import CopyDaySelector from './copyWorkoutModal/CopyDaySelector';
import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import { clearWorkoutItems } from '../ducks/calendarActions';
import { WEEKDAYS, countSelectedWeekdaysInRange } from './copyWorkoutModal/copyDateUtils';

const NO_DAYS_SELECTED = WEEKDAYS.reduce((days, day) => ({ ...days, [day.key]: false }), {});

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ModalTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const ModalDescription = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 20px;
`;

const StyledLink = styled('span')`
  color: #007FAE;
  text-decoration: underline;
  cursor: pointer;
`;

const DateContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const DateLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
`;

const DatePickerContainer = styled('div')`
  width: 48%;
`;

const OptionsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DaysBlock = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const DaysHeaderRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
`;

const DaysLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
`;

const HelperText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`;

const ClearButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: #FF0505;
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #D32F2F;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:disabled:hover {
    background: #FF0505;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.text};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const formatDateString = (d) => (d ? moment(d).format('YYYY-MM-DD') : '');

const ClearWorkoutItemsModal = ({ closeModal, modalData }) => {
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const currentUser = useSelector((state) => state.auth.data.currentUser);

  // Use modalData.date if provided (from week view), otherwise use selectedDate from Redux
  const initialDate = modalData?.date ? new Date(modalData.date) : new Date(selectedDate);

  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(initialDate);
  const [includeParentCalendars, setIncludeParentCalendars] = useState(false);
  const [clearWorkoutTitles, setClearWorkoutTitles] = useState(false);
  const [clearAllDays, setClearAllDays] = useState(true);
  const [selectedDays, setSelectedDays] = useState(NO_DAYS_SELECTED);

  /** Day options only apply to a multi-day range; a reversed range keeps the single-day form. */
  const isRange = differenceInCalendarDays(endDate, startDate) > 0;
  const isFilteringDays = isRange && !clearAllDays;
  const isSubmitDisabled = isFilteringDays
    && countSelectedWeekdaysInRange(startDate, endDate, selectedDays) === 0;

  const handleToggleParentCalendars = () => {
    setIncludeParentCalendars((prev) => !prev);
  };

  const handleToggleWorkoutTitles = () => {
    setClearWorkoutTitles((prev) => !prev);
  };

  const handleToggleClearAllDays = () => {
    if (!clearAllDays) {
      setSelectedDays(NO_DAYS_SELECTED);
    }
    setClearAllDays((prev) => !prev);
  };

  const handleToggleDay = (dayKey) => {
    setSelectedDays((prev) => ({ ...prev, [dayKey]: !prev[dayKey] }));
    setClearAllDays(false);
  };

  const handleClearItems = () => {
    const formattedStartDate = formatDateString(startDate);
    const formattedEndDate = formatDateString(endDate);
    const submissionObject = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      clearParents: includeParentCalendars,
      clearWorkoutInfo: clearWorkoutTitles,
    };
    /** The API treats a missing targetDays as every day, and rejects an empty array. */
    if (isFilteringDays) {
      submissionObject.targetDays = WEEKDAYS
        .filter((day) => selectedDays[day.key])
        .map((day) => day.dayIndex);
    }
    dispatch(clearWorkoutItems(
      currentUser.accountCode,
      submissionObject,
    ));
    closeModal();
  };

  const handleLearnMoreClick = () => {
    window.open('https://support.teambuildr.com', '_blank');
  };

  return (
    <ModalContainer>
      <ModalTitle>Clear Workout Items</ModalTitle>
      <ModalDescription>
        Recurring workout items cannot be cleared using this method.
        {' '}
        <StyledLink onClick={handleLearnMoreClick}>Click here to learn more.</StyledLink>
      </ModalDescription>

      <DateContainer>
        <DatePickerContainer>
          <DateLabel>Start Date</DateLabel>
          <TBDatePicker
            mobileFriendly
            activeDate={formatDateString(startDate)}
            setActiveDate={setStartDate}
            additionalStyles={{ margin: '8px', maxWidth: '100%' }}
            showDropShadow={false}
            showIcon={false}
            maxDate={null}
          />
        </DatePickerContainer>

        <DatePickerContainer>
          <DateLabel>End Date</DateLabel>
          <TBDatePicker
            mobileFriendly
            activeDate={formatDateString(endDate)}
            setActiveDate={setEndDate}
            additionalStyles={{ margin: '8px', maxWidth: '100%' }}
            showDropShadow={false}
            showIcon={false}
            maxDate={null}
            minDate={startDate}
          />
        </DatePickerContainer>
      </DateContainer>

      <OptionsContainer>
        <CopyCheckbox
          checked={includeParentCalendars}
          id='clear-items-parents'
          label='Clear items originating from parent calendars'
          onChange={handleToggleParentCalendars}
        />
        <CopyCheckbox
          checked={clearWorkoutTitles}
          id='clear-items-titles'
          label='Clear workout titles'
          onChange={handleToggleWorkoutTitles}
        />
        {isRange && (
          <CopyCheckbox
            checked={clearAllDays}
            id='clear-items-all-days'
            label='Clear all days in range'
            onChange={handleToggleClearAllDays}
          />
        )}
      </OptionsContainer>

      {isRange && (
        <DaysBlock>
          <DaysHeaderRow>
            <DaysLabel>Select days</DaysLabel>
            <HelperText>
              Selecting days will deselect &quot;Clear all days in range&quot;
            </HelperText>
          </DaysHeaderRow>
          <CopyDaySelector
            onSetDays={setSelectedDays}
            onToggleDay={handleToggleDay}
            selectedDays={selectedDays}
            showShortcuts={false}
          />
        </DaysBlock>
      )}

      <ButtonsContainer>
        <ClearButton disabled={isSubmitDisabled} onClick={handleClearItems}>
          CLEAR WORKOUT ITEMS
        </ClearButton>
        <CancelButton onClick={closeModal}>CANCEL</CancelButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ClearWorkoutItemsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.shape({
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isFromWeekView: PropTypes.bool,
  }),
};

ClearWorkoutItemsModal.defaultProps = {
  modalData: null,
};

export default ClearWorkoutItemsModal;
